'use client';

import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  OfferCardStatusEnum,
  StepupOfferGrid,
} from '@wwe-portal/ui/features/offer';
import { useStepupContext } from './context/stepup-context';
import { loadOfferStatuses, parserServerOfferData } from './lib/mapper';
import styles from './stepup-offer-dialog.module.css';
import { Dialog, DialogContent } from '@wwe-portal/ui/components/dialog';
import { ProgressBar } from '@wwe-portal/ui/components/progress';
import { useHideHelpshift } from '@wwe-portal/web/libs/helpshift/use-helpshift';

const StepupProgressBar = ({
  total,
  current,
}: {
  total: number;
  current: number;
}) => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <ProgressBar
          value={current}
          maxValue={total}
          className={styles.stepupProgress}
          barHeight={16}
        />
      </div>
    </div>
  );
};

export const StepupOfferDialog: FC = () => {
  const toolipRef = useRef<HTMLDivElement>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLDivElement | null>();
  useEffect(() => {
    setTooltipEl(toolipRef.current);
  }, []);
  const {
    closeModal,
    offers,
    stepupInfo,
    claimedItems: serverClaimedItems,
    onOfferClick,
    validatePurchase,
  } = useStepupContext();

  useHideHelpshift();

  const claimItems = useMemo(() => {
    return serverClaimedItems;
  }, [serverClaimedItems]);

  const offerList = useMemo(() => {
    const sortedOffer = offers?.sort(
      (offer1, offer2) => offer1.orderId - offer2.orderId
    );
    return loadOfferStatuses({
      offers: sortedOffer?.map(parserServerOfferData) ?? [],
      claimedItems: claimItems,
    });
  }, [offers, claimItems]);

  if (!stepupInfo) {
    return null;
  }
  if (!offerList || offerList.length === 0) {
    // Offers may get filered out due to the player not being a certain league level
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={(value) => {
        if (!value) closeModal();
      }}
    >
      <DialogContent
        data-id="stepup-dialog"
        variant="stepups"
        style={{
          maxWidth: 390,
          height: '100%',
          maxHeight: '100vh',
          top: '53%',
          overflow: 'scroll',
          background: 'none',
          paddingBottom: 100,
        }}
      >
        <StepupOfferGrid
          bodyBackgroundImage="/assets/images/special-offers/stepup-background.webp"
          onCloseModal={closeModal}
          offers={offerList.map((item) => {
            return {
              ...item.props,
              quantity: undefined,
              maxQuantity: undefined,
              endDate: undefined,
              tooltipContainer: tooltipEl,
              onClick: () => {
                onOfferClick(
                  { props: item.props, offer: item.origin },
                  item.origin.orderId
                );
              },
              onPurchaseClick: () => {
                onOfferClick(
                  { props: item.props, offer: item.origin },
                  item.origin.orderId
                );
                if (item.props.status !== OfferCardStatusEnum.CLAIM) return;
                validatePurchase({ stepupOffer: item.origin });
              },
            };
          })}
          banner={stepupInfo.imageUrl}
          claimedItems={claimItems}
          description={stepupInfo.description}
          endDate={stepupInfo.endDate}
          ProgressBar={StepupProgressBar}
        />
        <div ref={toolipRef} />
      </DialogContent>
    </Dialog>
  );
};
